import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/seo/Seo";
import Reveal from "@/components/ui-primitives/Reveal";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { articles, CANONICAL_ORIGIN, executive } from "@/data/site";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export default function Article() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  if (!article) return <Navigate to="/leadership/thought-leadership" replace />;

  const url = `${CANONICAL_ORIGIN}/leadership/thought-leadership/${article.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    headline: article.title,
    description: article.dek,
    image: [article.image],
    datePublished: article.date,
    dateModified: article.date,
    articleSection: article.category,
    author: {
      "@type": "Person",
      "@id": `${CANONICAL_ORIGIN}/leadership#adeshi-moffi`,
      name: executive.name,
      url: `${CANONICAL_ORIGIN}/leadership`,
    },
    publisher: {
      "@type": "Organization",
      "@id": `${CANONICAL_ORIGIN}/#organization`,
      name: executive.company,
      logo: {
        "@type": "ImageObject",
        url: `${CANONICAL_ORIGIN}/logo.png`,
      },
    },
    isPartOf: { "@id": `${CANONICAL_ORIGIN}/leadership/thought-leadership#blog` },
    keywords: article.category,
  };

  const idx = articles.findIndex((a) => a.slug === slug);
  const next = articles[(idx + 1) % articles.length];

  return (
    <>
      <Seo
        title={`${article.title} — Adeshi Moffi`}
        description={article.dek}
        path={`/leadership/thought-leadership/${article.slug}`}
        image={article.image}
        jsonLd={jsonLd}
      />

      <article>
        <section className="pt-28 md:pt-36 pb-8">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
            <Breadcrumbs
              items={[
                { label: "Home", to: "/" },
                { label: "Leadership", to: "/leadership" },
                { label: "Thought Leadership", to: "/leadership/thought-leadership" },
                { label: article.category },
              ]}
            />
            <Reveal>
              <div className="mt-10 section-num">{article.category}</div>
              <h1
                data-testid="article-title"
                className="h-display text-[2.6rem] md:text-[4.2rem] text-[#0A1128] mt-6 max-w-5xl leading-[0.98]"
              >
                {article.title}
              </h1>
              <p className="mt-6 body-lead max-w-3xl">{article.dek}</p>

              <div className="mt-10 border-t border-b border-[#E2E8F0] py-4 flex flex-wrap items-center gap-x-10 gap-y-2 text-xs uppercase tracking-[0.2em] text-[#475569] font-sans">
                <span>By <span className="text-[#0A1128]">{executive.name}</span></span>
                <span>{fmt(article.date)}</span>
                <span>{article.readTime}</span>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="pb-16">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
            <Reveal>
              <img
                src={article.image}
                alt={article.title}
                loading="lazy"
                className="w-full h-[380px] md:h-[520px] object-cover grayscale"
              />
            </Reveal>
          </div>
        </section>

        <section className="pb-24">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-3 no-print">
              <div className="md:sticky md:top-24">
                <div className="section-num">Author</div>
                <div className="mt-4 flex items-center gap-4">
                  <img
                    src={executive.headshot}
                    alt={executive.headshotAlt}
                    className="w-16 h-16 object-cover duotone-portrait"
                  />
                  <div>
                    <div className="h-editorial text-lg text-[#0A1128]">{executive.name}</div>
                    <div className="text-xs uppercase tracking-[0.16em] text-[#475569] font-sans mt-1">{executive.title}</div>
                  </div>
                </div>
                <Link to="/leadership/biography" data-testid="article-author-link" className="link-underline mt-6 inline-block text-xs uppercase tracking-[0.16em] font-sans">
                  Executive Biography
                </Link>
              </div>
            </div>

            <div className="col-span-12 md:col-span-9">
              <Reveal>
                <div className="prose-editorial max-w-3xl">
                  {article.body.map((b, i) => {
                    if (b.type === "h2") return <h2 key={i}>{b.text}</h2>;
                    if (b.type === "h3") return <h3 key={i}>{b.text}</h3>;
                    if (b.type === "blockquote") return <blockquote key={i}>{b.text}</blockquote>;
                    return <p key={i}>{b.text}</p>;
                  })}
                </div>
              </Reveal>

              <div className="mt-14 border-t border-[#E2E8F0] pt-8 flex flex-wrap items-center justify-between gap-4 no-print">
                <Link to="/leadership/thought-leadership" data-testid="article-back" className="btn-outline">
                  <ArrowLeft size={14} strokeWidth={1.5} /> All Perspectives
                </Link>
                <Link to={`/leadership/thought-leadership/${next.slug}`} data-testid="article-next" className="btn-ink">
                  Next: {next.title.slice(0, 42)}…
                </Link>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
